// Workflow modeli — backend gelene kadar mock. Bir akis: durumlar (states) + gecisler (transitions).
// Her gecisin rolleri, validatorleri ve post-action'lari (mail/SLA/webhook/n8n) vardir.

export type WfCategory = "new" | "active" | "pending" | "done" | "closed";

export interface WfState {
    id: string;
    name: string;
    color: string;
    category: WfCategory;
    customerVisible: boolean;
    customerLabel: string;   // musteriye gosterilen ad
}

export interface WfRoles { admin: boolean; manager: boolean; agent: boolean; customer: boolean; }
export interface WfValidators { assignee: boolean; group: boolean; resolutionNote: boolean; attachment: boolean; }
export interface WfPostActions { mail: boolean; slaStart: boolean; notifyOwner: boolean; webhook: boolean; n8n: boolean; }

export interface WfTransition {
    id: string;
    name: string;
    from: string;            // state id
    to: string;              // state id
    roles: WfRoles;
    validators: WfValidators;
    postActions: WfPostActions;
    avgMinutes: number;      // analitik: ortalama gecis suresi (dk)
}

export interface Workflow {
    id: string;
    name: string;
    ticketType: string;
    active: boolean;
    states: WfState[];
    transitions: WfTransition[];
}

const roles = (admin = true, manager = true, agent = true, customer = false): WfRoles => ({ admin, manager, agent, customer });
const val = (assignee = false, group = false, resolutionNote = false, attachment = false): WfValidators => ({ assignee, group, resolutionNote, attachment });
const pa = (mail = false, slaStart = false, notifyOwner = false, webhook = false, n8n = false): WfPostActions => ({ mail, slaStart, notifyOwner, webhook, n8n });

export const MOCK_WORKFLOWS: Workflow[] = [
    {
        id: "wf-tech",
        name: "Teknik Destek",
        ticketType: "Incident",
        active: true,
        states: [
            { id: "s1", name: "Yeni", color: "#3B82F6", category: "new", customerVisible: true, customerLabel: "Talebiniz Alındı" },
            { id: "s2", name: "İnceleniyor", color: "#6366F1", category: "active", customerVisible: true, customerLabel: "Uzmanlarımız İnceliyor" },
            { id: "s3", name: "Çalışılıyor", color: "#8B5CF6", category: "active", customerVisible: true, customerLabel: "İşlem Yapılıyor" },
            { id: "s4", name: "Müşteri Bekleniyor", color: "#F97316", category: "pending", customerVisible: true, customerLabel: "Yanıtınız Bekleniyor" },
            { id: "s5", name: "Çözüldü", color: "#10B981", category: "done", customerVisible: true, customerLabel: "Çözüldü" },
            { id: "s6", name: "Kapandı", color: "#6B7280", category: "closed", customerVisible: true, customerLabel: "Kapandı" },
        ],
        transitions: [
            { id: "t1", name: "İncelemeye Al", from: "s1", to: "s2", roles: roles(), validators: val(true), postActions: pa(true, true), avgMinutes: 15 },
            { id: "t2", name: "Çalışmaya Başla", from: "s2", to: "s3", roles: roles(), validators: val(true, true), postActions: pa(false, false, true), avgMinutes: 120 },
            { id: "t3", name: "Müşteriye Sor", from: "s3", to: "s4", roles: roles(), validators: val(), postActions: pa(true, false, true), avgMinutes: 240 },
            { id: "t4", name: "Çöz", from: "s3", to: "s5", roles: roles(true, true, true), validators: val(true, true, true), postActions: pa(true, false, true, true, true), avgMinutes: 360 },
            { id: "t5", name: "Kapat", from: "s5", to: "s6", roles: roles(true, true, false), validators: val(), postActions: pa(true), avgMinutes: 1440 },
        ],
    },
    {
        id: "wf-feature",
        name: "Özellik Talebi",
        ticketType: "Feature",
        active: true,
        states: [
            { id: "f1", name: "Yeni", color: "#3B82F6", category: "new", customerVisible: true, customerLabel: "Talebiniz Alındı" },
            { id: "f2", name: "Değerlendiriliyor", color: "#6366F1", category: "active", customerVisible: true, customerLabel: "Değerlendiriliyor" },
            { id: "f3", name: "Planlandı", color: "#0EA5E9", category: "active", customerVisible: true, customerLabel: "Planlandı" },
            { id: "f4", name: "Geliştiriliyor", color: "#8B5CF6", category: "active", customerVisible: true, customerLabel: "Geliştiriliyor" },
            { id: "f5", name: "Tamamlandı", color: "#10B981", category: "done", customerVisible: true, customerLabel: "Tamamlandı" },
        ],
        transitions: [
            { id: "ft1", name: "Değerlendir", from: "f1", to: "f2", roles: roles(), validators: val(), postActions: pa(true), avgMinutes: 60 },
            { id: "ft2", name: "Planla", from: "f2", to: "f3", roles: roles(true, true, false), validators: val(true), postActions: pa(true, false, true), avgMinutes: 480 },
            { id: "ft3", name: "Geliştirmeye Al", from: "f3", to: "f4", roles: roles(), validators: val(true, true), postActions: pa(false, true), avgMinutes: 2880 },
            { id: "ft4", name: "Tamamla", from: "f4", to: "f5", roles: roles(), validators: val(false, false, true), postActions: pa(true, false, true, true), avgMinutes: 4320 },
        ],
    },
    {
        id: "wf-finance",
        name: "Muhasebe",
        ticketType: "Finance",
        active: true,
        states: [
            { id: "m1", name: "Yeni", color: "#3B82F6", category: "new", customerVisible: true, customerLabel: "Talebiniz Alındı" },
            { id: "m2", name: "Kontrol Ediliyor", color: "#6366F1", category: "active", customerVisible: true, customerLabel: "Kontrol Ediliyor" },
            { id: "m3", name: "Onay Bekliyor", color: "#F97316", category: "pending", customerVisible: false, customerLabel: "İşleniyor" },
            { id: "m4", name: "Tamamlandı", color: "#10B981", category: "done", customerVisible: true, customerLabel: "Tamamlandı" },
        ],
        transitions: [
            { id: "mt1", name: "Kontrole Al", from: "m1", to: "m2", roles: roles(), validators: val(true), postActions: pa(true), avgMinutes: 30 },
            { id: "mt2", name: "Onaya Gönder", from: "m2", to: "m3", roles: roles(true, true, true), validators: val(true, true), postActions: pa(true, false, true), avgMinutes: 180 },
            { id: "mt3", name: "Onayla & Tamamla", from: "m3", to: "m4", roles: roles(true, true, false), validators: val(false, false, true), postActions: pa(true, false, true, true), avgMinutes: 360 },
        ],
    },
    {
        id: "wf-hr",
        name: "İnsan Kaynakları",
        ticketType: "HR",
        active: true,
        states: [
            { id: "h1", name: "Yeni", color: "#3B82F6", category: "new", customerVisible: true, customerLabel: "Talebiniz Alındı" },
            { id: "h2", name: "Değerlendiriliyor", color: "#6366F1", category: "active", customerVisible: true, customerLabel: "Değerlendiriliyor" },
            { id: "h3", name: "Onay Bekliyor", color: "#F97316", category: "pending", customerVisible: false, customerLabel: "İşleniyor" },
            { id: "h4", name: "Tamamlandı", color: "#10B981", category: "done", customerVisible: true, customerLabel: "Tamamlandı" },
        ],
        transitions: [
            { id: "ht1", name: "Değerlendir", from: "h1", to: "h2", roles: roles(true, true, true), validators: val(true), postActions: pa(true), avgMinutes: 60 },
            { id: "ht2", name: "Onaya Gönder", from: "h2", to: "h3", roles: roles(true, true, false), validators: val(true, true), postActions: pa(true, false, true), avgMinutes: 240 },
            { id: "ht3", name: "Tamamla", from: "h3", to: "h4", roles: roles(true, true, false), validators: val(false, false, true), postActions: pa(true, false, true, true), avgMinutes: 480 },
        ],
    },
    {
        id: "wf-sales",
        name: "Satış",
        ticketType: "Sales",
        active: false,
        states: [
            { id: "x1", name: "Yeni", color: "#3B82F6", category: "new", customerVisible: true, customerLabel: "Talebiniz Alındı" },
            { id: "x2", name: "Teklif Hazırlanıyor", color: "#6366F1", category: "active", customerVisible: true, customerLabel: "Teklif Hazırlanıyor" },
            { id: "x3", name: "Teklif Gönderildi", color: "#0EA5E9", category: "pending", customerVisible: true, customerLabel: "Teklifiniz Gönderildi" },
            { id: "x4", name: "Kazanıldı", color: "#10B981", category: "done", customerVisible: true, customerLabel: "Anlaşıldı" },
            { id: "x5", name: "Kaybedildi", color: "#EF4444", category: "closed", customerVisible: false, customerLabel: "Kapandı" },
        ],
        transitions: [
            { id: "xt1", name: "Teklif Hazırla", from: "x1", to: "x2", roles: roles(), validators: val(true), postActions: pa(true), avgMinutes: 120 },
            { id: "xt2", name: "Teklif Gönder", from: "x2", to: "x3", roles: roles(), validators: val(true, false, true), postActions: pa(true, true, true), avgMinutes: 240 },
            { id: "xt3", name: "Kazan", from: "x3", to: "x4", roles: roles(true, true, false), validators: val(), postActions: pa(true, false, true, true, true), avgMinutes: 2880 },
            { id: "xt4", name: "Kaybet", from: "x3", to: "x5", roles: roles(true, true, false), validators: val(false, false, true), postActions: pa(true), avgMinutes: 2880 },
        ],
    },
];

// Sure formatlama: dk -> "15 dk" / "2 sa" / "3 gün"
export function formatMinutes(min: number): string {
    if (min < 60) return `${min} dk`;
    if (min < 1440) return `${Math.round(min / 60)} sa`;
    return `${Math.round(min / 1440)} gün`;
}
